import {
  CARD_DECK_RADIUS,
} from './constants';

// --- Inline SVG star components ---

interface StarIconProps {
  fill?: string;
  size?: number;
}

function Star4Icon({ fill = '#FFF1BB', size = 10 }: StarIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none">
      <path
        d="M5 0L5.07386 0.199613C5.88415 2.38937 7.61063 4.11585 9.80039 4.92614L10 5L9.80039 5.07386C7.61063 5.88415 5.88415 7.61063 5.07386 9.80039L5 10L4.92614 9.80039C4.11585 7.61063 2.38937 5.88415 0.199613 5.07386L0 5L0.199613 4.92614C2.38937 4.11585 4.11585 2.38937 4.92614 0.199613L5 0Z"
        fill={fill}
      />
    </svg>
  );
}

function Star5Icon({ fill = '#FFF1BB', size = 13 }: StarIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 13 13" fill="none">
      <path
        d="M11.3856 1.72616L10.7499 5.61603L12.0442 9.33893L8.14825 9.93637L5.00751 12.3178L3.23541 8.79712L3.25708e-05 6.546L2.80074 3.77269L3.9419 3.06619e-05L7.44494 1.80667L11.3856 1.72616Z"
        fill={fill}
      />
    </svg>
  );
}

// --- Skeleton bar color ---
const SKELETON_BAR_COLOR = '#C7EDEF';

// --- Front skeleton card shadow (Figma value, distinct from live card shadow) ---
const SKELETON_CARD_SHADOW = '0px 4px 16px 0px rgba(100, 97, 255, 0.2)';

// --- Component ---

export default function SkeletonDeckCard() {
  return (
    <div
      className="flex flex-col bg-white overflow-hidden w-full aspect-[382/462] p-4 sm:p-[18px] md:p-5 lg:p-[22px]"
      style={{
        borderRadius: `${CARD_DECK_RADIUS.card}px`,
        boxShadow: SKELETON_CARD_SHADOW,
        gap: '10px',
      }}
    >
      {/* Image area — teal gradient with decorative dots and stars */}
      <div
        className="relative overflow-hidden flex-shrink-0 aspect-[382/180]"
        style={{ borderRadius: `${CARD_DECK_RADIUS.image}px` }}
      >
        {/* Base fill */}
        <div className="absolute inset-0" style={{ backgroundColor: SKELETON_BAR_COLOR }} />

        {/* Blur blob — creates the teal-to-light gradient */}
        <div
          className="absolute rounded-full"
          style={{
            width: '120%',
            height: '120%',
            top: '-40%',
            left: '-10%',
            backgroundColor: '#01A7C8',
            filter: 'blur(50px)',
          }}
        />

        {/* White dots — 4 total */}
        <div className="absolute w-1 h-1 bg-white rounded-full" style={{ left: '9%', top: '26%' }} />
        <div className="absolute w-1 h-1 bg-white rounded-full" style={{ left: '85%', top: '27%' }} />
        <div className="absolute w-1.5 h-1.5 bg-white rounded-full" style={{ left: '10%', top: '72%' }} />
        <div className="absolute w-1.5 h-1.5 bg-white rounded-full" style={{ left: '76%', top: '82%' }} />

        {/* Star icons — 3 total */}
        <div className="absolute" style={{ left: '47%', top: '25%' }}>
          <Star4Icon fill="white" size={7} />
        </div>
        <div className="absolute" style={{ left: '83%', top: '50%' }}>
          <Star4Icon fill="white" size={10} />
        </div>
        <div className="absolute" style={{ left: '85%', top: '65%' }}>
          <Star5Icon fill="white" size={10} />
        </div>
      </div>

      {/* Title bar */}
      <div
        className="w-full h-[22px] rounded-[6px]"
        style={{ backgroundColor: SKELETON_BAR_COLOR }}
      />

      {/* Metadata row — 3 bars */}
      <div className="flex gap-4">
        <div
          className="h-[12px] w-[54px] rounded-[8px]"
          style={{ backgroundColor: SKELETON_BAR_COLOR }}
        />
        <div
          className="h-[12px] w-[93px] rounded-[8px]"
          style={{ backgroundColor: SKELETON_BAR_COLOR }}
        />
        <div
          className="h-[12px] w-[116px] rounded-[8px]"
          style={{ backgroundColor: SKELETON_BAR_COLOR }}
        />
      </div>

      {/* Description lines — 3 bars */}
      <div className="flex flex-col gap-[8px]">
        <div
          className="w-full h-[12px] rounded-[8px]"
          style={{ backgroundColor: SKELETON_BAR_COLOR }}
        />
        <div
          className="w-full h-[12px] rounded-[8px]"
          style={{ backgroundColor: SKELETON_BAR_COLOR }}
        />
        <div
          className="w-[73%] h-[12px] rounded-[8px]"
          style={{ backgroundColor: SKELETON_BAR_COLOR }}
        />
      </div>

      {/* Spacer — pushes button to bottom */}
      <div className="flex-1" />

      {/* Button bar */}
      <div
        className="w-full h-12 sm:h-[50px] md:h-[52px] lg:h-14 rounded-[8px]"
        style={{ backgroundColor: SKELETON_BAR_COLOR }}
      />
    </div>
  );
}
